import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
  nanoid,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import {
  showCustomNotification,
  showError,
} from '@vk-hr-tek/core/notifications';

import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';
import {
  DashboardService,
  GetAccountWidgetQuickActionsQuery,
  GetAccountWidgetQuickActionsParams,
  UpdateAccountWidgetQuickActionsParams,
  UpdateAccountWidgetQuickActionsQuery,
  UpdateAccountWidgetQuickActionsRequest,
  GetAccountWidgetQuickActionsResponse,
} from '@app/gen/dashboard';
import { ThunkExtra, ThunkValuesAndActions } from '@app/store';

import { SettingsState, SettingsWithRootState } from '../../settings.state';

export const updateQuickActions = createAsyncThunk<
  unknown,
  ThunkValuesAndActions<{
    items: UpdateAccountWidgetQuickActionsRequest['actions'];
    params: UpdateAccountWidgetQuickActionsParams;
    query?: UpdateAccountWidgetQuickActionsQuery;
  }>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateQuickActions',
  async (
    { values, actions },
    { rejectWithValue, extra: { inject }, dispatch },
  ) => {
    try {
      const service = await inject(DashboardService);

      await service.updateAccountWidgetQuickActions({
        params: values.params,
        body: { actions: values.items },
        query: { company_id: values?.query?.company_id },
      });

      actions.resolve(null);
      dispatch(
        showCustomNotification([
          {
            title: 'Виджет успешно изменен',
            message: 'Быстрые действия',
            type: 'info',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showError(
          'Не удалось сохранить виджет\nПопробуйте, пожалуйста, еще раз',
        ),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getQuickActions = createAsyncThunk<
  GetAccountWidgetQuickActionsResponse,
  {
    query?: GetAccountWidgetQuickActionsQuery;
    params: GetAccountWidgetQuickActionsParams;
  },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getQuickActions',
  async ({ query, params }, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = await inject(DashboardService);

      const result = await service.getAccountWidgetQuickActions({
        query,
        params,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsQuickActionsReducer = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getQuickActions.pending, (state) => {
    state.companyQuickActions.status = 'loading';
  });
  builder.addCase(getQuickActions.fulfilled, (state, { payload }) => {
    state.companyQuickActions.activeActions =
      payload.actions.map((action) => {
        return { ...action, id: nanoid() };
      }) || [];
    state.companyQuickActions.availableActions =
      payload.available_actions.map((action) => {
        return { ...action, id: nanoid() };
      }) || [];

    state.companyQuickActions.status = 'complete';
    state.companyQuickActions.isException = !!payload.company_content_id;
    state.companyQuickActions.hasParent = !!payload.source_widget_id;
  });
  builder.addCase(getQuickActions.rejected, (state, { payload, error }) => {
    state.companyQuickActions = {
      status: 'failed',
      error:
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError),
      availableActions: [],
      activeActions: [],
      isException: false,
      hasParent: false,
    };
  });
};
